import React, {useEffect, useMemo, useState}		from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';

import Avatar										from '../ressources/components/Avatar';
import Select										from '../ressources/components/Select';
import Badge										from '../ressources/components/Badge';
import Checkbox										from '../ressources/components/Checkbox';
import Button										from '../ressources/components/Button';
import Modal										from '../ressources/components/Modal';
import Input										from '../ressources/components/Input';
import DevicesBadges								from '../ressources/components/Badge/DevicesBadges';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import Icon											from '../ressources/components/Icon';
import ModalAssign									from '../ressources/components/ModalAssign';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';
import Tabs											from '../ressources/components/Tabs';
import CheckboxGroup								from '../ressources/components/Checkbox/CheckboxGroup';
import ModalMdmCommand								from '../ressources/components/ModalMdmCommand';

import {useAppDispatch, useAppSelector}				from '../app/hooks';
import {getCompanyUsersApi}							from '../redux/companyUsers/companyUsers';
import {getDevicesApi, getMdmDevicesApi}			from '../redux/devices/devices';
import {PutMdmDeviceCommand}						from '../redux/devices/interfaceMdm';
import {User, UserNotNull}								from '../redux/user/interface';
import {TabsValues}									from '../ressources/components/Tabs/interface';
import {InputValues}								from '../ressources/components/Input/interface';
import {LanguageType}								from '../ressources/language/type';
import {DevicesState}								from '../redux/devices/interface';

import {ft_formatUserName}							from '../ressources/functions/format';
import {ft_selectDataUser}							from '../ressources/functions/selectData';

import img_offboarding								from '../ressources/images/offboarding.png';
import img_plusSquare								from '../ressources/images/icon/plus-square.svg';
import img_userMinus								from '../ressources/images/icon/user-minus.svg';

import * as API										from '../ressources/API';
import LANGUAGE										from '../ressources/language';

import './Offboarding.css';

interface ImportData {
	user: User,
	hris: keyof LanguageType['component']['externalApi'],
	_id: string
}

export default function Offboarding() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const devicesMdm = useAppSelector(state => state.devices.mdm);
	const company = useAppSelector(state => state.company.data);
	const orders = useAppSelector(state => state.order.data);
	const lang = useAppSelector(state => state.settings.lang);
	// let notificationId = useAppSelector(state => state.notifications.lastId);

	const [view, set_view] = useState('home');
	const [hris, set_hris] = useState<(keyof LanguageType['component']['externalApi'])[]>([]);
	const [hrisUsers, set_hrisUsers] = useState<ImportData[]>([]);
	const [selected, set_selected] = useState<{[key: string]: boolean}>({});

	const [modal, set_modal] = useState(false);
	const [modalAdd, set_modalAdd] = useState(false);
	const [modalMdm, set_modalMdm] = useState<keyof LanguageType['devices']['mdm']['actions'] | undefined>(undefined);

	const [addStep, set_addStep] = useState<TabsValues>(1);
	const [addList, set_addList] = useState<string[]>([]);
	const [contractEnd, set_contractEnd] = useState<InputValues>('');
	const [contractHours, set_contractHours] = useState<InputValues>('');
	const [deviceAction, set_deviceAction] = useState<InputValues>('moveStock');
	const [mdmAction, set_mdmAction] = useState<PutMdmDeviceCommand | undefined>(undefined);

	// const [loadPost, set_loadPost] = useState(false);

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const devicesCount: number = useMemo(() => {
		if (addList.length === 0)
			return 0;
		return devicesWithLines.filter(e => addList.some(u => u === e.user)).length;
	}, [addList]);

	const mdmList = useMemo(() => {
		const check = (checkName: keyof DevicesState['mdm']) => {
			return devices.filter(e =>
				addList.some(u => u === e.user) && devicesMdm[checkName].some(f => f.serial === e.serial)
			);
		};

		return {
			jamf:   check('jamf') || [],
			vmware: check('vmWare') || []
		};
	}, [addList]);

	const mdmTotal = useMemo(() => mdmList.jamf.length + mdmList.vmware.length, [mdmList]);

	const usersOffboarding: UserNotNull[] = useMemo(() => {
		const result: UserNotNull[] = [];

		users.forEach((userData) => {
			if (!userData?._id || userData.data?.offboarding?.end)
				return;
			const data = JSON.parse(JSON.stringify(userData));

			const hrisFind = hrisUsers.find(f => f._id === data._id);

			if (hrisFind?.user?.contractEnd)
				data.contractEnd = hrisFind.user.contractEnd;

			if (!data.contractEnd && !hrisFind?.user?.contractEnd)
				return;

			const userContractEnd = moment(data.contractEnd);

			if (userContractEnd.isBefore(moment()) && !devicesWithLines.some(e => e.user && e.user === data._id))
				return;
			if (!userContractEnd.isBefore(moment().add(1, 'month')))
				return;

			result.push(data);
		});

		return result.sort((a, b) => moment(a.contractEnd).valueOf() - moment(b.contractEnd).valueOf());
	}, [users, devicesWithLines, hrisUsers]);

	useEffect(() => {
		dispatch(getDevicesApi(user));
		dispatch(getMdmDevicesApi({name: 'jamf', user}));
		dispatch(getMdmDevicesApi({name: 'vmWare', user}));

		API.getUserOauth(user)
		.then((response) => {
			if (response.data)
				set_hris(response.data);
		});
	}, []);

	useEffect(() => set_view(params.view || 'home'), [params.view]);

	useEffect(() => {
		if (hris.length)
			getHrisUsers();
	}, [hris]);

	function getHrisUsers(i = 0, data: ImportData[] = []) {
		if (!hris[i])
			return;

		API.importUserHris(hris[i], user)
		.then((response) => {
			if (!response?.data)
				return getHrisUsers(i + 1, data);

			const validImports: ImportData[] = [...data];

			response?.data?.forEach((imported: User) => {
				if (!imported?.email || !imported.contractEnd)
					return;

				const findUser = users.find(find => find?.email.toLowerCase() === imported.email.toLowerCase() || ft_formatUserName(find) === ft_formatUserName(imported));

				if (!findUser)
					return;

				validImports.push({
					_id:  findUser._id,
					hris: hris[i],
					user: imported
				});
			});

			set_hrisUsers(validImports);
			getHrisUsers(i + 1, validImports);
		});
	}

	function addOffboarding(forceUsers?: string[], isRemove = false) {
		const useDate = contractEnd ? moment(contractEnd, 'DD/MM/YYYY') : undefined;

		if (useDate && contractHours) {
			const [h, m] = contractHours.toString().split(':');

			useDate.set({hour: parseInt(h, 10), minute: parseInt(m, 10)});
		}

		API.putUsersOffboarding({
			mdmAction,
			users:       forceUsers || addList,
			action:      isRemove ? '' : deviceAction as string,
			contractEnd: useDate ? useDate.toDate() : undefined,
			vmWare:      mdmList.vmware.map(e => ({
				_id:    e._id,
				serial: e.serial,
				user:   e.user as string,
				isWin:  !!devicesMdm.vmWare.find(f => f.serial === e.serial)?.fullData?.platform?.includes('Win')
			})),
			jamf:        mdmList.jamf.map(e => ({
				_id:    e._id,
				serial: e.serial,
				user:   e.user as string,
				id:     devicesMdm.jamf.find(f => f.serial === e.serial)?.id
			}))
		}, user)
		.then((response) => {
			if (!response.status)
				return;

			set_addStep(1);
			set_addList([]);
			set_selected({});
			set_view('home');
			set_contractEnd('');
			set_deviceAction('moveStock');
			set_contractHours('');
			dispatch(getCompanyUsersApi(user));
			navigate('/users/offboarding');
		});
	}

	function renderHome() {
		const onClickAll = () => {
			const newSelected: typeof selected = {};

			if (!allSelected)
				usersOffboarding.forEach(e => newSelected[e._id] = true);

			set_selected(newSelected);
		};
		const render: React.ReactElement[] = [];
		const selectedCount = Object.values(selected).filter(e => e).length;
		const allSelected = selectedCount === usersOffboarding.length;

		usersOffboarding.forEach((userData, i) => {
			let status: 'complete' | 'unstart' = 'unstart';

			if (userData.data?.offboarding?.action || !devicesWithLines.some(device => userData && device.user === userData?._id))
				status = 'complete';

			render.push(
				<tr
					key={`hris-${i}`}
					style={{cursor: 'pointer'}}
					className={selected[userData._id] ? 'tableRow selected' : ''}
					onClick={() => set_selected({...selected, [userData._id]: !selected[userData._id]})}
				>
					<td>
						<Checkbox active={selected[userData._id]} />
					</td>
					<td>
						<div className={'flex'} style={{alignItems: 'center', gap: 8}}>
							<Avatar size={40} user={userData} />
							{ft_formatUserName(userData)}
						</div>
					</td>
					<td>{userData.contractEnd ? moment(userData.contractEnd).format('DD/MM/YYYY') : ''}</td>
					<td>
						<span className={`deviceStatus ${status}`}>{TEXTE.users.offboarding[status]}</span>
					</td>
					<td>
						<DevicesBadges gap={0} userData={userData} order={orders} />
					</td>
					<td align={'right'}>
						<Select
							style={{alignSelf: 'flex-end'}}
							placeholderClassname={'weight-600 gray-600'}
							placeholder={TEXTE.users.offboarding.start}
							data={[
								{value: 'start', render: TEXTE.users.offboarding.start},
								{value: 'cancel', render: TEXTE.users.offboarding.cancel}
							]}
							onChange={(val) => {
								if (val === 'cancel')
									return addOffboarding([userData._id], true);
								set_addList([userData._id]);
								set_view('add');
							}}
						/>
					</td>
				</tr>
			);
		});

		return (
			<>
				<div className={'globalDataContainerItem'} style={{padding: '16px 24px'}}>
					<img src={img_offboarding} alt={''} />
					<span className={'text-lg weight-600 gray-900'}>{TEXTE.users.offboarding.title}</span>
					<Badge color={'gray'} text={usersOffboarding.length} />
				</div>

				<br />
				<div className={'globalDataContainerItem column flex'} style={{overflow: 'auto'}}>
					<div className={'flex-row'} style={{gap: 8}}>
						<Button
							size={'sm'}
							type={'secondary-gray'}
							disabled={selectedCount === 0}
							onClick={() => {
								set_addList(Object.entries(selected).filter(e => e[1]).map(e => e[0]));
								set_view('add');
							}}
						>
							{TEXTE.users.offboarding.start}
						</Button>
						<Button
							size={'sm'}
							type={'danger-reverse'}
							disabled={selectedCount === 0}
							onClick={() => addOffboarding(Object.entries(selected).filter(e => e[1]).map(e => e[0], true))}
						>
							{TEXTE.users.offboarding.cancel}
						</Button>
					</div>
					<table className={'importTable'}>
						<thead>
							<tr style={{textAlign: 'left'}}>
								<th style={{width: 50}}>
									{usersOffboarding.length > 0 && <Checkbox onChange={onClickAll} active={allSelected} />}
								</th>
								<th>{TEXTE.global.name}</th>
								<th>{TEXTE.users.offboarding.endDate}</th>
								<th>{TEXTE.users.offboarding.status}</th>
								<th>{TEXTE.global.devices}</th>
								<th align={'right'}>{TEXTE.global.action}</th>
							</tr>
						</thead>
						<tbody>
							{render}
						</tbody>
					</table>

					<ButtonStickyContainer>
						<Button
							fullWidth
							iconBefore={img_plusSquare}
							onClick={() => {
								set_addStep(1);
								set_addList([]);
								set_selected({});
								set_contractEnd('');
								set_deviceAction('moveStock');
								set_contractHours('');
								navigate('/users/offboarding/add');
							}}
						>
							{TEXTE.users.offboarding.button}
						</Button>
					</ButtonStickyContainer>
				</div>
			</>
		);
	}

	function renderAdd() {
		return (
			<>
				<div className={'globalAddHeader'}>
					<Icon
						icon={img_userMinus}
						iconColor={'#000000'}
					/>
					<div className={'globalAddHeaderText'}>
						<span>{TEXTE.users.offboarding.add.title}</span>
						<span>{TEXTE.users.offboarding.add.text}</span>
					</div>
				</div>

				<br />
				<div className={'globalDataContainerItem column'}>
					<Tabs
						step
						actual={addStep}
						onChange={set_addStep}
						data={[
							{value: 1, render: TEXTE.menu.users},
							{value: 2, render: TEXTE.users.offboarding.add.deviceManagement}
						]}
					/>

					{addStep === 1 &&
						<>
							<Select
								required
								data={ft_selectDataUser(users)}
								onClick={() => set_modal(true)}
								title={TEXTE.users.offboarding.add.selectUsers}
								value={addList.map(e => ft_formatUserName(users.find(u => u?._id === e)))}
							/>

							<MultiInputContainer>
								<Input
									required
									type={'date'}
									value={contractEnd}
									onChange={set_contractEnd}
									title={TEXTE.users.offboarding.add.leaveDate}
								/>
								<Input
									required
									type={'time'}
									value={contractHours}
									onChange={set_contractHours}
									title={TEXTE.users.offboarding.add.leaveHours}
								/>
							</MultiInputContainer>
						</>
					}

					{addStep === 2 &&
						<>
							<CheckboxGroup
								actual={deviceAction}
								onChange={set_deviceAction}
								title={TEXTE.users.offboarding.add.deviceAction}
								afterTitle={<Badge color={'success'} text={`${devicesCount} ${TEXTE.global.devices}`} />}
								data={[
									{value: 'moveStock', title: TEXTE.devices.inReserve},
									{
										disabled: true,
										value:    'sell',
										text:     TEXTE.users.offboarding.add.sellText,
										title:    TEXTE.users.offboarding.add.sellToValala
									}
								]}
							/>

							<CheckboxGroup
								title={TEXTE.users.offboarding.add.mdm}
								actual={mdmAction?.commmandName || 'nothing'}
								onChange={val => (val === 'nothing' ? set_mdmAction(undefined) : set_modalMdm(val as keyof LanguageType['devices']['mdm']['actions']))}
								afterTitle={
									!company?.isPro ?
										<Badge size={'sm'} color={'gray'} text={'Pro'} />
										:
										<Badge color={mdmTotal ? 'success' : 'warning'} text={`${mdmTotal} ${TEXTE.global.devices}`} />
								}
								data={[
									{
										disabled: !mdmTotal,
										value:    'nothing',
										title:    TEXTE.users.offboarding.add.nothing
									},
									{
										disabled: !mdmTotal,
										value:    'reset',
										title:    TEXTE.users.offboarding.add.erase,
										text:     TEXTE.users.offboarding.add.eraseText
									},
									{
										disabled: !mdmList.jamf?.length,
										value:    'deleteAccount',
										text:     TEXTE.users.offboarding.add.removeText,
										title:    TEXTE.users.offboarding.add.deleteAccount
									}
								]}
							/>
						</>
					}
				</div>
				<ButtonStickyContainer>
					<Button
						fullWidth
						type={'tertiary-gray'}
						onClick={() => {
							navigate('/users/offboarding');
							set_view('home');
						}}
					>
						{TEXTE.global.cancel}
					</Button>
					<Button
						fullWidth
						disabled={!addList.length || !contractEnd || !contractHours}
						onClick={() => (addStep === 1 ? set_addStep(2) : addOffboarding())}
					>
						{addStep === 1 ? TEXTE.global.next : TEXTE.global.confirm}
					</Button>
				</ButtonStickyContainer>
			</>
		);
	}

	return (
		<div className={'globalDataContainer'}>
			{view === 'home' && renderHome()}

			{view === 'add' && renderAdd()}
			<br />

			{modal &&
				<ModalAssign
					multiple
					modalRight
					actualId={addList}
					onClose={() => set_modal(false)}
					onConfirm={() => set_modal(false)}
					confirmText={TEXTE.global.validateSelection}
					onChange={(_, list) => set_addList(list || [])}
					header={TEXTE.users.offboarding.add.selectUsers}
				/>
			}

			{modalAdd &&
				<Modal
					// confirmLoad={loadPost}
					// confirmButton={addUsers}
					onClose={() => set_modalAdd(false)}
					cancelButton={() => set_modalAdd(false)}
				>
					{/* <span className={'text-lg weight-600 gray-900'}>{TEXTE.users.offboarding.addModal}</span> */}
					<br />
					<Input
						type={'date'}
						// value={contractStart}
						placeholder={'JJ/MM/AAAA'}
						style={{marginBottom: 300}}
						// onChange={set_contractStart}
					/>
				</Modal>
			}

			{modalMdm &&
				<ModalMdmCommand
					mdmName={'jamf'}
					modalCommand={modalMdm}
					returnData={set_mdmAction}
					editData={mdmList.jamf[0]}
					onClose={() => set_modalMdm(undefined)}
					date={{
						date:  contractEnd,
						hours: contractHours
					}}
				/>
			}
		</div>
	);
}